import { useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { css, ThemeProvider as EmotionThemeProvider, useTheme } from '@emotion/react';
import { DisplayField } from '@veeva/corgix/field';
import { Select } from '@veeva/corgix/select';
import { isMobile } from '../services/DeviceInformation';
import mobileContentWidthCSS from '../constants/mobileContentWidthCSS';
import labelLineHeightCSS from '../constants/labelLineHeightCSS';

const propTypes = {
    children: PropTypes.node,
    disabled: PropTypes.bool,
    displayFieldCSS: PropTypes.shape({}),
    errorMessage: PropTypes.string,
    label: PropTypes.string,
    menuFontSize: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onMenuOpen: PropTypes.func,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    selectCSS: PropTypes.shape({}),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
};

const SelectField = ({
    label,
    children,
    errorMessage,
    onChange,
    onMenuOpen,
    value,
    disabled = false,
    placeholder,
    readOnly = false,
    displayFieldCSS,
    selectCSS,
    menuFontSize = '1.333rem',
    ...htmlProps
}) => {
    const { selectFieldInputWidth } = useTheme();
    const [selectWidth, setSelectWidth] = useState(undefined);
    const [key, forceUpdate] = useReducer((x) => x + 1, 0);
    const handleSelectRef = (node) => {
        setSelectWidth(node?.getBoundingClientRect().width);
    };

    const handleChange = (e, newValue, option) => {
        forceUpdate();
        onChange(newValue, option);
    };

    const selectFieldCSS = ({
        veevaIdErrorLabelBufferHeight,
        silverDark,
        veevaIdDisplayFieldLabelBottomMargin,
    }) => {
        return [
            css`
                margin-bottom: 0;

                label {
                    padding: 0;
                }
            `,
            readOnly &&
                css`
                    input {
                        color: ${silverDark};
                    }
                `,
            css`
                > div > div:nth-of-type(2) {
                    padding-top: 0.333rem;
                    padding-bottom: 0;
                    min-height: ${veevaIdErrorLabelBufferHeight};
                }
                width: 100%;

                label {
                    padding: 0;
                    ${labelLineHeightCSS}
                }
            `,
            isMobile() &&
                css`
                    & > div > div > div {
                        justify-content: center;

                        & > div {
                            ${mobileContentWidthCSS};
                        }
                    }
                `,
            veevaIdDisplayFieldLabelBottomMargin &&
                css`
                    label {
                        margin-bottom: ${veevaIdDisplayFieldLabelBottomMargin};
                    }
                `,
            displayFieldCSS,
        ];
    };

    const mobileOverlayProps = isMobile()
        ? {
              closeOnScroll: false,
              fitInViewport: false,
              placement: 'top',
          }
        : {};

    const select = (
        <EmotionThemeProvider
            theme={{
                inputWidthMD: selectFieldInputWidth,
                menuSpacing: '1rem',
                menuSpacingVariant1: '1rem',
                menuFontSize,
            }}
        >
            <Select
                key={key}
                value={value}
                placeholder={placeholder}
                onChange={handleChange}
                onMenuOpen={onMenuOpen}
                disabled={disabled}
                nodeRef={handleSelectRef}
                overlayProps={{
                    css:
                        isMobile() &&
                        css`
                            max-width: ${selectWidth}px;
                            // Overrides Menu max height,
                            // which has no token.
                            & > div {
                                max-height: 25vh;
                            }
                        `,
                    collision: false,
                    ...mobileOverlayProps,
                }}
                css={({ redDefault }) => [
                    css`
                        & > div {
                            width: ${selectFieldInputWidth};
                            height: 3.333rem;
                            border-radius: 8px;
                        }
                    `,
                    isMobile() &&
                        css`
                            & > div {
                                ${mobileContentWidthCSS};
                            }
                        `,
                    errorMessage &&
                        css`
                            & > div {
                                border-color: ${redDefault};
                            }
                        `,
                    selectCSS,
                ]}
                size="md"
                data-target={htmlProps['data-target']}
            >
                {children}
            </Select>
        </EmotionThemeProvider>
    );

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                line-height: 1.1666rem;
            `}
        >
            <DisplayField css={selectFieldCSS} label={label}>
                {select}
            </DisplayField>
            {errorMessage && (
                <div
                    css={({ redDefault }) => {
                        return css`
                            color: ${redDefault};
                            margin-top: 0.333rem;
                        `;
                    }}
                >
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

SelectField.propTypes = propTypes;

export default SelectField;
